import type {
  CountryDto,
  ProductReturnRateItemDto,
} from "@babackapp/private-api-client/types";

export type ProductExportRow = {
  productId: string;
  product: string;
  variantId: string;
  variant: string;
  sku: string;
  ordersCount: number;
  returnsCount: number;
  returnRate: number;
};

export type CountryExportRow = {
  countryCode: string;
  country: string;
  ordersCount: number;
  returnsCount: number;
  returnRate: number;
};

export const PRODUCT_EXPORT_KEYS = [
  "productId",
  "product",
  "variantId",
  "variant",
  "sku",
  "ordersCount",
  "returnsCount",
  "returnRate",
] as const satisfies readonly (keyof ProductExportRow)[];

export const COUNTRY_EXPORT_KEYS = [
  "countryCode",
  "country",
  "ordersCount",
  "returnsCount",
  "returnRate",
] as const satisfies readonly (keyof CountryExportRow)[];

export function escapeCsvValue(value: unknown): string {
  if (value == null) return "";
  const str = value.toString();
  if (/[";,\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildCsvContent<T>(
  rows: T[],
  keys: readonly (keyof T & string)[],
): string {
  const header = keys.join(";");
  if (rows.length === 0) return header;

  const body = rows
    .map((row) => keys.map((key) => escapeCsvValue(row[key])).join(";"))
    .join("\n");
  return `${header}\n${body}`;
}

export function downloadCsv(content: string, filename: string): void {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildProductRows(
  products: ProductReturnRateItemDto[],
): ProductExportRow[] {
  const rows: ProductExportRow[] = [];

  for (const product of products) {
    if (product.variants && product.variants.length > 0) {
      for (const variant of product.variants) {
        rows.push({
          productId: product.productId,
          product: product.title,
          variantId: variant.variantId,
          variant: variant.title,
          sku: variant.sku ?? "",
          ordersCount: variant.ordersCount,
          returnsCount: variant.returnsCount,
          returnRate: variant.returnRate,
        });
      }
    } else {
      rows.push({
        productId: product.productId,
        product: product.title,
        variantId: "",
        variant: "",
        sku: product.sku ?? "",
        ordersCount: product.ordersCount,
        returnsCount: product.returnsCount,
        returnRate: product.returnRate,
      });
    }
  }

  return rows;
}

// An empty selection means "no country filter", so every country is exported.
// The backend sends an unlabelled row with an empty countryCode for the lines
// with no shipping address; it is never selectable, so a country filter drops
// it, which is what we want.
export function buildCountryRows(
  countries: CountryDto[],
  selectedCountryCodes: string[],
  noCountryLabel: string,
): CountryExportRow[] {
  const selected = new Set(
    selectedCountryCodes.map((code) => code.toUpperCase()),
  );

  return countries
    .filter(
      (country) =>
        selected.size === 0 || selected.has(country.countryCode.toUpperCase()),
    )
    .map((country) => ({
      countryCode: country.countryCode,
      country: country.countryCode ? country.country : noCountryLabel,
      ordersCount: country.ordersCount,
      returnsCount: country.returnsCount,
      returnRate: country.returnRate,
    }));
}
